import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import OrganisationCombobox from "@/components/pages/platform-admin/organisations/OrganisationCombobox";
import { AssignAgentFormController } from "@/hooks/useAssignAgentForm";
import { AiListingOrganisation, AiListingVisibility } from "@/model/interface/AiAgent";
import Organisation from "@/model/interface/Organisation";
import AssignAgentForm from "../components/AssignAgentForm";

export interface WizardListingDraft {
  displayName: string;
  visibility: AiListingVisibility;
  monthlyFee: string;
  organisations: AiListingOrganisation[];
}

export const EMPTY_LISTING_DRAFT: WizardListingDraft = {
  displayName: "",
  visibility: "PUBLIC",
  monthlyFee: "0",
  organisations: [],
};

export default function WizardDeployStep({
  controller,
  canCreateListing,
  listingEnabled,
  onListingEnabledChange,
  listing,
  onListingChange,
}: {
  controller: AssignAgentFormController;
  canCreateListing: boolean;
  listingEnabled: boolean;
  onListingEnabledChange: (enabled: boolean) => void;
  listing: WizardListingDraft;
  onListingChange: (listing: WizardListingDraft) => void;
}) {
  const deployOrganisation = controller.organisation;

  const handleVisibilityChange = (nextVisibility: AiListingVisibility) => {
    const shouldSeedOrganisation =
      nextVisibility === "PRIVATE" && listing.organisations.length === 0 && deployOrganisation !== null;
    onListingChange({
      ...listing,
      visibility: nextVisibility,
      organisations: shouldSeedOrganisation
        ? [{ id: deployOrganisation.id, name: deployOrganisation.name }]
        : listing.organisations,
    });
  };

  const addListingOrganisation = (organisation: Organisation | null) => {
    if (!organisation || listing.organisations.some((entry) => entry.id === organisation.id)) {
      return;
    }
    onListingChange({
      ...listing,
      organisations: [...listing.organisations, { id: organisation.id, name: organisation.name }],
    });
  };

  const removeListingOrganisation = (organisationId: string) => {
    onListingChange({
      ...listing,
      organisations: listing.organisations.filter((entry) => entry.id !== organisationId),
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Deploy to an organisation</CardTitle>
          <CardDescription>
            Grants a complimentary agent, free for the organisation and excluded from billing. For a paid agent, use a
            private listing below and let the organisation acquire it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AssignAgentForm controller={controller} lockSource />
        </CardContent>
      </Card>

      {canCreateListing && (
        <Card>
          <CardHeader>
            <CardTitle>Marketplace listing</CardTitle>
            <CardDescription>
              Optional. Lets an organisation acquire a paid agent built on this template from its own marketplace.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="wizard-listing-toggle">List in the marketplace</Label>
              <Switch id="wizard-listing-toggle" checked={listingEnabled} onCheckedChange={onListingEnabledChange} />
            </div>

            {listingEnabled && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Who can see it</Label>
                    <Select
                      value={listing.visibility}
                      onValueChange={(nextVisibility) => handleVisibilityChange(nextVisibility as AiListingVisibility)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PUBLIC">Public: every organisation</SelectItem>
                        <SelectItem value="PRIVATE">Private: only the organisations you name</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Display name</Label>
                    <Input
                      value={listing.displayName}
                      onChange={(event) => onListingChange({ ...listing, displayName: event.target.value })}
                      placeholder="Template name"
                    />
                  </div>
                </div>

                {listing.visibility === "PRIVATE" ? (
                  <>
                    <div className="space-y-1.5">
                      <Label>Organisations *</Label>
                      <OrganisationCombobox value={null} onChange={addListingOrganisation} />
                      {listing.organisations.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {listing.organisations.map((organisation) => (
                            <Badge key={organisation.id} variant="secondary" className="gap-1">
                              {organisation.name ?? organisation.id}
                              <button type="button" onClick={() => removeListingOrganisation(organisation.id)}>
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Add as many as you like. Only these organisations see this listing in their marketplace.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Monthly price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        className="max-w-[200px]"
                        value={listing.monthlyFee}
                        onChange={(event) => onListingChange({ ...listing, monthlyFee: event.target.value })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Every organisation on this listing pays this price per month for each agent it acquires. For
                        a different price per organisation, create a separate private listing on the same template.
                        Usage per minute follows each organisation's pricing plan.
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Public listings charge the template's tier price per month and each organisation's plan rate per
                    minute.
                  </p>
                )}
              </>
            )}

            <p className="text-sm text-muted-foreground">
              Perks, limits, cover media and extra organisations are edited in the Marketplace Listings tab.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
